/**
 * One Pinia store backing all four resource collections (targets / assets /
 * findings / reports). Each collection lazy-loads on first read and falls
 * back to bundled mock data when the backend is unreachable, so the demo
 * site keeps working without a live API.
 *
 * The `view*` getters expose the same mock-friendly shape the views were
 * originally written against (nameEn / discoveredEn / statusClass / etc),
 * so swapping in real DTOs is a one-line import change in each view.
 */

import { defineStore } from 'pinia'
import { targetsApi, type TargetDTO } from '@/api/targets'
import { assetsApi, type AssetDTO } from '@/api/assets'
import { findingsApi, type FindingDTO } from '@/api/findings'
import { reportsApi, type ReportDTO, type ReportExportDTO } from '@/api/reports'
import { scansApi, type ScanJobDTO, type ScanCreate } from '@/api/scans'

import { targets as targetsMock } from '@/data/targets'
import { assets as assetsMock } from '@/data/assets'
import { findingsFull as findingsMock, recentFindings as recentFindingsMock } from '@/data/findings'
import { reportsData as reportsMock, exportHistory as exportsMock } from '@/data/reports'
import type { Severity, ReportState, Finding, TargetRow, AssetRow, ReportRow } from '@/data/types'

interface ResourceState<T> {
  items: T[]
  loaded: boolean
  loading: boolean
  error: string | null
  usingMock: boolean
}

interface State {
  targets: ResourceState<TargetDTO>
  assets: ResourceState<AssetDTO>
  findings: ResourceState<FindingDTO>
  reports: ResourceState<ReportDTO>
  reportExports: ResourceState<ReportExportDTO>
  scans: ResourceState<ScanJobDTO>
  scansPollTimer: number | null
}

function emptyState<T>(): ResourceState<T> {
  return { items: [], loaded: false, loading: false, error: null, usingMock: false }
}

// ============================================================================
// Mock → DTO adapters (cached fallback when backend is unreachable)
// ============================================================================

function targetsMockAsDTO(): TargetDTO[] {
  return targetsMock.map((t) => ({
    id: t.id,
    name: t.name,
    name_en: t.nameEn || null,
    scope: t.scope,
    scope_en: t.scopeEn || null,
    status: t.statusEn === 'Scanning' ? 'scanning' : t.statusEn === 'Idle' ? 'idle' : 'inactive',
    tags: t.tags,
    assets_count: t.assets,
    endpoints_count: t.endpoints,
    findings_count: t.findings,
    last_scan_at: t.last_scan,
    extra: {
      last_scan_en: t.last_scanEn,
      status_display: t.status,
      status_class: t.statusClass,
    },
  }))
}

function assetsMockAsDTO(): AssetDTO[] {
  return assetsMock.map((a, idx) => ({
    id: idx + 1,
    target_id: null,
    host: a.host,
    title: a.title,
    ip: a.ip,
    port: a.port,
    techs: a.techs,
    labels: a.labels,
    findings_count: a.findings,
    max_sev: a.maxSev,
    notes: null,
  }))
}

function findingsMockAsDTO(): FindingDTO[] {
  return findingsMock.map((f, idx) => ({
    id: idx + 1,
    target_id: null,
    asset_id: null,
    sev: f.sev,
    title: f.title,
    title_en: f.titleEn || null,
    host: f.host,
    cve: f.cve || null,
    cvss: f.cvss || null,
    kev: f.kev || false,
    status: f.status || 'suspect',
    description: null,
    matcher: {},
    evidence: {},
    created_at: f.discovered,
  }))
}

function reportsMockAsDTO(): ReportDTO[] {
  return reportsMock.map((r, idx) => ({
    id: idx + 1,
    title: r.t,
    title_en: r.tEn || null,
    platform: r.p,
    platform_en: r.pEn || null,
    state: r.state,
    severity: r.s,
    finding_ids: [],
    body_md: null,
    output_language: 'zh',
    notes: null,
    updated_at: r.u,
  }))
}

function exportsMockAsDTO(): ReportExportDTO[] {
  return exportsMock.map((e, idx) => ({
    id: idx + 1,
    report_id: null,
    filename: e.n,
    fmt: e.f,
    size_bytes: 0,
    summary: e.c,
    created_at: e.u,
  }))
}

// ============================================================================
// DTO → view-friendly shape (matches the original mock shape used in templates)
// ============================================================================

function targetStatusDisplay(s: string): { zh: string; en: string; cls: string } {
  if (s === 'scanning') return { zh: '扫描中', en: 'Scanning', cls: 'chip-info' }
  if (s === 'idle') return { zh: '空闲', en: 'Idle', cls: 'chip-soft' }
  return { zh: '未启', en: 'Inactive', cls: 'chip-soft' }
}

function dtoToTargetRow(t: TargetDTO): TargetRow {
  const status = targetStatusDisplay(t.status)
  const extra = t.extra as { last_scan_en?: string; status_display?: string; status_class?: string } | null
  return {
    id: t.id,
    name: t.name,
    nameEn: t.name_en || t.name,
    scope: t.scope,
    scopeEn: t.scope_en || t.scope,
    status: extra?.status_display || status.zh,
    statusEn: status.en,
    statusClass: extra?.status_class || status.cls,
    assets: t.assets_count,
    endpoints: t.endpoints_count,
    findings: t.findings_count,
    tags: t.tags,
    last_scan: t.last_scan_at || '未运行',
    last_scanEn: extra?.last_scan_en || (t.last_scan_at ? t.last_scan_at : 'Not run'),
  }
}

function dtoToAssetRow(a: AssetDTO): AssetRow {
  return {
    host: a.host,
    title: a.title || '',
    ip: a.ip || '',
    port: a.port,
    techs: a.techs,
    labels: a.labels,
    findings: a.findings_count,
    maxSev: a.max_sev as Severity,
    discovered: a.notes || '',
    discoveredEn: a.notes || '',
  }
}

function dtoToFinding(f: FindingDTO): Finding {
  return {
    id: f.id,
    sev: f.sev as Severity,
    title: f.title,
    titleEn: f.title_en || f.title,
    host: f.host || '',
    cve: f.cve || '—',
    cvss: f.cvss || '—',
    kev: f.kev,
    status: f.status,
    discovered: f.created_at,
    discoveredEn: f.created_at,
  }
}

function dtoToReportRow(r: ReportDTO): ReportRow {
  return {
    t: r.title,
    tEn: r.title_en || r.title,
    p: r.platform,
    pEn: r.platform_en || r.platform,
    n: r.finding_ids.length,
    s: r.severity as Severity,
    u: r.updated_at,
    uEn: r.updated_at,
    state: r.state as ReportState,
  }
}

// ============================================================================
// Store
// ============================================================================

export const useResourcesStore = defineStore('resources', {
  state: (): State => ({
    targets: emptyState(),
    assets: emptyState(),
    findings: emptyState(),
    reports: emptyState(),
    reportExports: emptyState(),
    scans: emptyState(),
    scansPollTimer: null,
  }),

  getters: {
    viewTargets(state): TargetRow[] {
      return state.targets.items.map(dtoToTargetRow)
    },
    viewAssets(state): AssetRow[] {
      return state.assets.items.map(dtoToAssetRow)
    },
    viewFindings(state): Finding[] {
      return state.findings.items.map(dtoToFinding)
    },
    viewReports(state): ReportRow[] {
      return state.reports.items.map(dtoToReportRow)
    },
    activeScans(state): ScanJobDTO[] {
      return state.scans.items.filter((s) => s.status === 'queued' || s.status === 'running')
    },
    viewRecentFindings(state): Finding[] {
      // If backend hasn't loaded, fall back to the hand-curated 6-row demo set.
      if (state.findings.usingMock && state.findings.items.length === 0) {
        return recentFindingsMock
      }
      const list = state.findings.usingMock
        ? recentFindingsMock
        : state.findings.items.slice(0, 6).map(dtoToFinding)
      return list
    },
  },

  actions: {
    async fetchTargets() {
      if (this.targets.loaded || this.targets.loading) return
      this.targets.loading = true
      try {
        this.targets.items = await targetsApi.list()
        this.targets.usingMock = false
      } catch {
        this.targets.items = targetsMockAsDTO()
        this.targets.usingMock = true
      } finally {
        this.targets.loaded = true
        this.targets.loading = false
      }
    },

    async fetchAssets() {
      if (this.assets.loaded || this.assets.loading) return
      this.assets.loading = true
      try {
        this.assets.items = await assetsApi.list()
        this.assets.usingMock = false
      } catch {
        this.assets.items = assetsMockAsDTO()
        this.assets.usingMock = true
      } finally {
        this.assets.loaded = true
        this.assets.loading = false
      }
    },

    async fetchFindings() {
      if (this.findings.loaded || this.findings.loading) return
      this.findings.loading = true
      try {
        this.findings.items = await findingsApi.list()
        this.findings.usingMock = false
      } catch {
        this.findings.items = findingsMockAsDTO()
        this.findings.usingMock = true
      } finally {
        this.findings.loaded = true
        this.findings.loading = false
      }
    },

    async fetchReports() {
      if (this.reports.loaded || this.reports.loading) return
      this.reports.loading = true
      try {
        this.reports.items = await reportsApi.list()
        this.reports.usingMock = false
      } catch {
        this.reports.items = reportsMockAsDTO()
        this.reports.usingMock = true
      } finally {
        this.reports.loaded = true
        this.reports.loading = false
      }
    },

    async fetchScans(force = false) {
      if (!force && (this.scans.loaded || this.scans.loading)) return
      this.scans.loading = true
      try {
        this.scans.items = await scansApi.list({ limit: 20 })
        this.scans.usingMock = false
      } catch {
        this.scans.items = []
        this.scans.usingMock = true
      } finally {
        this.scans.loaded = true
        this.scans.loading = false
      }
    },

    async refreshScansSilent() {
      try {
        this.scans.items = await scansApi.list({ limit: 20 })
        this.scans.usingMock = false
      } catch {
        /* leave previous items in place */
      }
    },

    async startScan(targetId: number, preset: ScanCreate['preset'] = 'public_recon') {
      const job = await scansApi.create({ target_id: targetId, preset })
      // prepend so the active-scans bar surfaces it immediately
      this.scans.items = [job, ...this.scans.items.filter((s) => s.id !== job.id)]
      this.startScansPolling()
      return job
    },

    startScansPolling() {
      if (this.scansPollTimer !== null) return
      const tick = async () => {
        await this.refreshScansSilent()
        if (this.activeScans.length === 0) {
          // last poll: also refresh the asset list so newly-found subdomains appear
          this.assets.loaded = false
          await this.fetchAssets()
          this.targets.loaded = false
          await this.fetchTargets()
          this.stopScansPolling()
        }
      }
      this.scansPollTimer = window.setInterval(tick, 3000)
    },

    stopScansPolling() {
      if (this.scansPollTimer !== null) {
        window.clearInterval(this.scansPollTimer)
        this.scansPollTimer = null
      }
    },

    async fetchReportExports() {
      if (this.reportExports.loaded || this.reportExports.loading) return
      this.reportExports.loading = true
      try {
        this.reportExports.items = await reportsApi.exportHistory()
        this.reportExports.usingMock = false
      } catch {
        this.reportExports.items = exportsMockAsDTO()
        this.reportExports.usingMock = true
      } finally {
        this.reportExports.loaded = true
        this.reportExports.loading = false
      }
    },
  },
})

export type { Severity, ReportState }
